import type { FailedSaleTransaction } from '~/api/graphql/generated/types'
import i18n from '~/locales/i18n'
/**
 * Transaction Failure Reason names from backend enum TransactionFailureReason
 * Maps to static_transaction_failure_reason table
 */
export enum TransactionFailureReasonName {
  DEVICE_OFFLINE = 'DEVICE_OFFLINE',
  DEVICE_NOT_FOUND = 'DEVICE_NOT_FOUND',
  DEVICE_TIMEOUT = 'DEVICE_TIMEOUT',
  DEVICE_BUSY = 'DEVICE_BUSY',
  DEVICE_BATTERY_LOW = 'DEVICE_BATTERY_LOW',
  CANCELLED_BY_DEVICE = 'CANCELLED_BY_DEVICE',
  CANCELLED_BY_API = 'CANCELLED_BY_API',
  PROCESSOR_DECLINED = 'PROCESSOR_DECLINED',
  GATEWAY_REJECTED = 'GATEWAY_REJECTED',
  SETTLEMENT_DECLINED = 'SETTLEMENT_DECLINED',
  UNKNOWN = 'UNKNOWN',
  TRANSACTION_NOT_SETTLED = 'TRANSACTION_NOT_SETTLED',
}

/**
 * Error categories for grouping similar error types
 */
export enum PaymentErrorCategory {
  CONNECTIVITY = 'connectivity',
  ISSUER_DECLINE = 'issuer_decline',
  USER_CANCEL = 'user_cancel',
  CONNECTIVITY_DOWN = 'connectivity_down',
  UNKNOWN = 'unknown',
}

/**
 * Maps transaction failure reason name to error category
 */
const FAILURE_REASON_TO_CATEGORY_MAP: Record<
  TransactionFailureReasonName,
  PaymentErrorCategory
> = {
  [TransactionFailureReasonName.DEVICE_OFFLINE]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_NOT_FOUND]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_TIMEOUT]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_BUSY]: PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.DEVICE_BATTERY_LOW]:
    PaymentErrorCategory.CONNECTIVITY,
  [TransactionFailureReasonName.CANCELLED_BY_DEVICE]:
    PaymentErrorCategory.USER_CANCEL,
  [TransactionFailureReasonName.CANCELLED_BY_API]:
    PaymentErrorCategory.USER_CANCEL,
  [TransactionFailureReasonName.GATEWAY_REJECTED]:
    PaymentErrorCategory.CONNECTIVITY_DOWN,
  [TransactionFailureReasonName.PROCESSOR_DECLINED]:
    PaymentErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonName.SETTLEMENT_DECLINED]:
    PaymentErrorCategory.ISSUER_DECLINE,
  [TransactionFailureReasonName.UNKNOWN]: PaymentErrorCategory.UNKNOWN,
  [TransactionFailureReasonName.TRANSACTION_NOT_SETTLED]:
    PaymentErrorCategory.UNKNOWN,
}

const getFailureReasonCategory = (
  failureReasonName: string,
): PaymentErrorCategory =>
  FAILURE_REASON_TO_CATEGORY_MAP[
    failureReasonName as TransactionFailureReasonName
  ] ?? PaymentErrorCategory.UNKNOWN

/**
 * Gets translated message for error category (without account credits)
 * Translations are computed dynamically, so they update when i18n language changes.
 * For CONNECTIVITY category, failureReasonName is passed as errorCode (TransactionFailureReasonName).
 * @internal Exported for testing fallback when failureReasonName is undefined
 */
export const getCategoryTranslationMessage = (
  category: PaymentErrorCategory,
  failureReasonName?: string,
): string => {
  switch (category) {
    case PaymentErrorCategory.ISSUER_DECLINE:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE',
      )
    case PaymentErrorCategory.CONNECTIVITY:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY',
        { errorCode: failureReasonName ?? 'Payment Processing Issue' },
      )
    case PaymentErrorCategory.UNKNOWN:
      return i18n.t('Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
    case PaymentErrorCategory.USER_CANCEL:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_USER_CANCEL',
      )
    case PaymentErrorCategory.CONNECTIVITY_DOWN:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_DOWN',
      )
    default:
      return i18n.t('Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
  }
}

/**
 * Gets translated message for error category (with account credits applied)
 * Translations are computed dynamically, so they update when i18n language changes.
 * For CONNECTIVITY category, failureReasonName is passed as errorCode (TransactionFailureReasonName).
 * @internal Exported for testing fallback when failureReasonName is undefined
 */
export const getCategoryTranslationMessageWithCredits = (
  category: PaymentErrorCategory,
  failureReasonName?: string,
): string => {
  switch (category) {
    case PaymentErrorCategory.ISSUER_DECLINE:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_ISSUER_DECLINE',
      )
    case PaymentErrorCategory.CONNECTIVITY:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_WITH_CREDITS',
        { errorCode: failureReasonName ?? 'Payment Processing Issue' },
      )
    case PaymentErrorCategory.UNKNOWN:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN_WITH_CREDITS',
      )
    case PaymentErrorCategory.USER_CANCEL:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_USER_CANCEL_WITH_CREDITS',
      )
    case PaymentErrorCategory.CONNECTIVITY_DOWN:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_CONNECTIVITY_DOWN',
      )
    default:
      return i18n.t(
        'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN_WITH_CREDITS',
      )
  }
}

const getTransactionFailureTranslationMessage = (
  failureReasonName: string,
  accountCreditsApplied: boolean,
): string => {
  const category = getFailureReasonCategory(failureReasonName)

  try {
    return accountCreditsApplied
      ? getCategoryTranslationMessageWithCredits(category, failureReasonName)
      : getCategoryTranslationMessage(category, failureReasonName)
  } catch {
    // Fallback to unknown message if translation fails
    return accountCreditsApplied
      ? getCategoryTranslationMessageWithCredits(PaymentErrorCategory.UNKNOWN)
      : getCategoryTranslationMessage(PaymentErrorCategory.UNKNOWN)
  }
}

/**
 * Extracts failure reason name from FailedSaleTransaction
 * Uses the name property which matches the backend TransactionFailureReason enum
 */
export const extractFailureReasonName = (
  transaction: FailedSaleTransaction,
): string | null => {
  const failureReason = transaction.error?.failureReason

  if (!failureReason?.name) {
    return null
  }

  return failureReason.name
}

/**
 * Maps a FailedSaleTransaction to a user-friendly error message
 */
export const getTransactionFailureMessage = (
  transaction: FailedSaleTransaction,
  accountCreditsApplied?: boolean,
): string => {
  const failureReasonName = extractFailureReasonName(transaction)

  if (failureReasonName === null) {
    // Fallback to generic message if we can't determine failure reason
    const fallbackMessage = accountCreditsApplied
      ? i18n.t(
          'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN_WITH_CREDITS',
        )
      : i18n.t('Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TRANSACTION_UNKNOWN')
    return fallbackMessage
  }

  return getTransactionFailureTranslationMessage(
    failureReasonName,
    accountCreditsApplied || false,
  )
}

/**
 * Extracts backend error message from FailedSaleTransaction for error doggo
 * Returns backend message if available, otherwise returns null
 */
export const getTransactionFailureBackendMessage = (
  transaction: FailedSaleTransaction,
): string | null => transaction.error?.message ?? null

/**
 * Maps account credit application failure to user-friendly error message
 */
export const getAccountCreditFailureMessage = (errorReason: string): string =>
  i18n.t('Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.ACCOUNT_CREDIT_FAILURE', {
    reason: errorReason,
  })

/**
 * Maps terminal initiation failure to user-friendly error message
 */
export const getTerminalInitiationFailureMessage = (
  accountCreditsApplied?: boolean,
): string => {
  if (accountCreditsApplied) {
    return i18n.t(
      'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TERMINAL_INITIATION_FAILURE_WITH_CREDITS',
    )
  }

  return i18n.t(
    'Dialogs:ADD_PAYMENT_DIALOG_V2.ERRORS.TERMINAL_INITIATION_FAILURE',
  )
}
